import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "@/i18n/LanguageContext";
import Reveal from "@/components/site/Reveal";
import { RevealTextInstant } from "@/components/site/RevealText";
import { PROCESS_STEPS } from "@/lib/data";

function Step({ index, id, active }) {
  const { t } = useLang();
  const step = t.process.steps[id];
  return (
    <div
      data-testid={`process-step-${id}`}
      className="grid grid-cols-12 gap-6 md:gap-10 py-14 md:py-24 border-t border-[#E5E5EA]"
    >
      <div className="col-span-2 md:col-span-2">
        <span className="font-mono text-[10px] tracking-widest uppercase text-[#86868B] block mb-3">Step</span>
        <motion.span
          className="block font-mono text-6xl md:text-8xl leading-none"
          animate={{ color: active ? "#0055FF" : "#0A0A0A", opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          0{index + 1}
        </motion.span>
      </div>
      <div className="col-span-10 md:col-span-6">
        <h2 className="font-serif text-3xl md:text-6xl tracking-tight leading-[1.02]">{step.title}</h2>
      </div>
      <div className="col-span-12 md:col-span-4">
        <p className="font-sans text-base md:text-lg text-[#86868B] leading-relaxed">{step.body}</p>
      </div>
    </div>
  );
}

export default function ProcessPage() {
  const { t } = useLang();
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef, offset: ["start start", "end end"] });
  const barY = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);
  const [activeStep, setActiveStep] = React.useState(0);

  React.useEffect(() => {
    return scrollYProgress.on("change", (v) => {
      const idx = Math.min(PROCESS_STEPS.length - 1, Math.max(0, Math.floor(v * PROCESS_STEPS.length)));
      setActiveStep(idx);
    });
  }, [scrollYProgress]);

  return (
    <div data-testid="page-process" className="pt-32 md:pt-40">
      <section className="mx-auto max-w-[1440px] px-6 md:px-10 pb-16 md:pb-24">
        <div className="grid grid-cols-12 gap-6 mb-10">
          <div className="col-span-12 md:col-span-3 az-tag"><span className="az-tag__dot" /><span>{t.process.kicker}</span></div>
          <div className="col-span-12 md:col-span-3 md:col-start-10 md:text-right font-mono text-[10px] tracking-widest uppercase text-[#86868B]">
            05 / Steps
          </div>
        </div>
        <RevealTextInstant
          as="h1"
          lines={[t.process.title]}
          className="font-serif tracking-tighter leading-[0.94] text-[10vw] md:text-[6vw] lg:text-[5.5rem]"
          lineClassName="block"
        />
        <div className="mt-10 grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-7 md:col-start-6">
            <Reveal delay={0.15}>
              <p className="font-sans text-lg md:text-xl leading-relaxed text-[#86868B] max-w-[52ch]">
                {t.process.lead}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section ref={containerRef} className="relative bg-white">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 relative">
          {/* Vertical progress bar */}
          <div className="hidden md:block absolute left-10 top-0 bottom-0 w-px bg-[#E5E5EA]" aria-hidden="true">
            <motion.div style={{ height: barY }} className="w-px bg-[#0055FF] origin-top" />
          </div>

          {PROCESS_STEPS.map((id, i) => (
            <Step key={id} id={id} index={i} active={activeStep === i} />
          ))}
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#F5F5F7] border-t border-[#E5E5EA]">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 flex flex-col md:flex-row items-start md:items-end justify-between gap-8">
          <h3 className="font-serif text-4xl md:text-6xl tracking-tighter max-w-[24ch] leading-[0.98]">
            {t.home.contactTitle}
          </h3>
          <Link to="/contact" className="az-btn" data-testid="process-contact-cta">
            <span>{t.nav.cta}</span>
            <span className="az-btn__arrow" aria-hidden="true">→</span>
          </Link>
        </div>
      </section>
    </div>
  );
}
